import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Trophy, Calendar, Users, Plus, Trash2, Edit2, Check, X, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { leagueAPI } from '../services/api';
import TournamentBracket from '../components/TournamentBracket';

const League = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // UI State
  const [activeLeagueType, setActiveLeagueType] = useState('unilig'); // 'unilig' | 'prolig'
  const [activeDivision, setActiveDivision] = useState('div_1'); // 'div_1' | 'div_2'

  // Store bracket state separately for each division
  const [bracketSettings, setBracketSettings] = useState({
    div_1: null, // 16, 8, or null
    div_2: null
  });

  // Data State
  const [seasons, setSeasons] = useState([]);
  const [displayedSeason, setDisplayedSeason] = useState(null);
  const [groups, setGroups] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState({});
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSeasonId, setSelectedSeasonId] = useState('auto');

  // Admin form states
  const [showSeasonForm, setShowSeasonForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [editingScore, setEditingScore] = useState(null);
  const [scoreForm, setScoreForm] = useState({ score_a: 0, score_b: 0 });
  const [customTeamInputs, setCustomTeamInputs] = useState({}); // { groupId: 'Name' }
  const [selectedTeamInputs, setSelectedTeamInputs] = useState({}); // { groupId: 'teamId' }
  const [expandedGroups, setExpandedGroups] = useState({}); // { groupId: boolean }

  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const [seasonForm, setSeasonForm] = useState({
    name: '',
    year: new Date().getFullYear(),
    league_type: 'unilig',
    division: 'div_1',
    format: 'groups_son16',
    group_count: 4
  });
  const [groupForm, setGroupForm] = useState({
    name: '',
    stage: 'group',
    division: 'div_1',
    order: 0
  });
  const [matchForm, setMatchForm] = useState({
    team_a_id: '', team_b_id: '', group_id: '', match_date: '',
    team_a_custom_name: '', team_b_custom_name: '',
    isCustomA: false, isCustomB: false
  });

  // Initial Load
  const loadSeasons = async () => {
    try {
      setLoading(true);
      const seasonsRes = await leagueAPI.getSeasons();
      setSeasons(seasonsRes.data);

      if (isAdmin) {
        const teamsRes = await leagueAPI.getTeams();
        setTeams(teamsRes.data.teams || teamsRes.data || []);
      }
    } catch (err) {
      console.error('Failed to load seasons:', err);
      setError('Failed to load league data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSeasons();
  }, [isAdmin]);

  // When seasons, activeLeagueType or activeDivision changes, find the relevant season
  useEffect(() => {
    if (seasons.length > 0) {
      if (selectedSeasonId !== 'auto') {
        const found = seasons.find(s => s.id === selectedSeasonId);
        // Only keep selected season if it matches the current type and division
        if (found && found.league_type === activeLeagueType && found.division === activeDivision) {
          setDisplayedSeason(found);
          return;
        } else {
          setSelectedSeasonId('auto');
        }
      }

      // Default logic: Find active season for this league type AND division
      let relevantSeason = seasons.find(s =>
        s.league_type === activeLeagueType &&
        s.division === activeDivision &&
        s.status === 'active'
      );

      // If no active season, find most recent one for this combo
      if (!relevantSeason) {
        relevantSeason = seasons
          .filter(s => s.league_type === activeLeagueType && s.division === activeDivision)
          .sort((a, b) => b.year - a.year)[0];
      }

      setDisplayedSeason(relevantSeason || null);
    }
  }, [activeLeagueType, activeDivision, seasons, selectedSeasonId]);

  // When displayedSeason changes, load its details
  useEffect(() => {
    const loadSeasonDetails = async () => {
      if (!displayedSeason) {
        setGroups([]);
        setMatches([]);
        setStandings({});
        return;
      }

      try {
        const [groupsRes, matchesRes] = await Promise.all([
          leagueAPI.getGroups(displayedSeason.id),
          leagueAPI.getMatches(displayedSeason.id, null)
        ]);

        setGroups(groupsRes.data);
        setMatches(matchesRes.data);

        // Load standings
        const standingsMap = {};
        for (const group of groupsRes.data) {
          try {
            const standingsRes = await leagueAPI.getStandings(group.id);
            standingsMap[group.id] = standingsRes.data;
          } catch (e) {
            console.error(`Failed to load standings for group ${group.id}:`, e);
          }
        }
        setStandings(standingsMap);
      } catch (err) {
        console.error('Failed to load season details:', err);
      }
    };

    loadSeasonDetails();
  }, [displayedSeason]);

  const refreshData = async () => {
    if (displayedSeason) {
      const [groupsRes, matchesRes] = await Promise.all([
        leagueAPI.getGroups(displayedSeason.id),
        leagueAPI.getMatches(displayedSeason.id, null)
      ]);
      setGroups(groupsRes.data);
      setMatches(matchesRes.data);
      const standingsMap = {};
      for (const group of groupsRes.data) {
        try {
          const standingsRes = await leagueAPI.getStandings(group.id);
          standingsMap[group.id] = standingsRes.data;
        } catch (e) { console.error(e); }
      }
      setStandings(standingsMap);
    }
  };

  const handleCreateSeason = async (e) => {
    e.preventDefault();
    try {
      await leagueAPI.createSeason(seasonForm);
      setShowSeasonForm(false);
      setSeasonForm({
        name: '',
        year: new Date().getFullYear(),
        league_type: activeLeagueType,
        division: activeDivision,
        format: 'groups_son16',
        group_count: 4
      });
      loadSeasons();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create season');
    }
  };

  const handleTogglePublish = async () => {
    if (!displayedSeason) return;
    try {
      await leagueAPI.updateSeason(displayedSeason.id, {
        is_published: !displayedSeason.is_published
      });
      loadSeasons();
    } catch (err) {
      setError('Failed to update season visibility');
    }
  };

  const handleDeleteSeason = async () => {
    if (!displayedSeason) return;
    if (!window.confirm(`"${displayedSeason.name}" sezonunu ve içindeki tüm grup/maçları silmek istediğinizden emin misiniz?`)) return;

    try {
      await leagueAPI.deleteSeason(displayedSeason.id);
      setSelectedSeasonId('auto');
      loadSeasons();
    } catch (err) {
      setError('Sezon silinemedi');
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!displayedSeason) return;
    try {
      await leagueAPI.createGroup({
        ...groupForm,
        season_id: displayedSeason.id
      });
      setShowGroupForm(false);
      setGroupForm({ name: '', stage: 'group', division: 'div_1', order: 0 });
      refreshData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleAddTeamToGroup = async (groupId, teamData) => {
    try {
      const payload = typeof teamData === 'string'
        ? { team_id: teamData }
        : teamData;
      await leagueAPI.addTeamToGroup(groupId, payload);
      refreshData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add team');
    }
  };

  const handleRemoveMember = async (groupId, memberId) => {
    try {
      await leagueAPI.removeMemberFromGroup(groupId, memberId);
      refreshData();
    }
    catch (err) { setError('Failed to remove team'); }
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();
    if (!displayedSeason) return;
    try {
      await leagueAPI.createMatch({
        ...matchForm,
        season_id: displayedSeason.id,
        match_date: matchForm.match_date || null,
        // Only send updates if empty string
        team_a_custom_name: matchForm.isCustomA ? matchForm.team_a_custom_name : null,
        team_b_custom_name: matchForm.isCustomB ? matchForm.team_b_custom_name : null,
        team_a_id: matchForm.isCustomA ? null : matchForm.team_a_id,
        team_b_id: matchForm.isCustomB ? null : matchForm.team_b_id
      });
      setShowMatchForm(false);
      setMatchForm({ team_a_id: '', team_b_id: '', group_id: '', match_date: '' });
      refreshData();
    } catch (err) { setError('Failed to create match'); }
  };

  const handleUpdateScore = async (matchId) => {
    try {
      await leagueAPI.updateMatchScore(matchId, scoreForm);
      setEditingScore(null);
      refreshData();
    } catch (err) { setError('Failed to update score'); }
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Delete match?')) return;
    try { await leagueAPI.deleteMatch(matchId); refreshData(); }
    catch (err) { setError('Failed to delete match'); }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Bu grubu silmek istediğinizden emin misiniz? Grubun içindeki tüm eşleşmeler de silinecektir.')) return;
    try {
      await leagueAPI.deleteGroup(groupId);
      refreshData();
    } catch (err) {
      setError('Grup silinemedi');
    }
  };

  // Filter teams for match creation based on selected group
  const availableMatchTeams = useMemo(() => {
    if (!matchForm.group_id) return teams;
    const selectedGroup = groups.find(g => g.id === matchForm.group_id);
    return selectedGroup
      ? teams.filter(t => selectedGroup.teams.some(gt => gt.team_id === t.id))
      : teams;
  }, [matchForm.group_id, groups, teams]);

  // Filtered Lists based on activeDivision
  const filteredGroups = groups.filter(g => g.division === activeDivision && g.stage === 'group');
  const filteredMatches = matches.filter(m => {
    if (m.group_id) {
      const grp = groups.find(g => g.id === m.group_id);
      return grp && grp.division === activeDivision;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Restrict access for non-admins to "Coming Soon" within the layout
  const NonAdminContent = () => (
    <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4 py-12 border border-dashed border-gray-800 rounded-lg bg-[#0a0a0b]/50">
      <div className="bg-yellow-500/10 p-6 rounded-full mb-6 animate-pulse">
        <Trophy className="w-16 h-16 text-yellow-500" />
      </div>
      <h1 className="text-3xl font-bold text-white mb-4">Yakında</h1>
      <p className="text-gray-400 max-w-md">
        Lig sayfası şu anda hazırlık aşamasında. Fikstürler, puan durumu ve istatistikler çok yakında burada olacak!
      </p>
    </div>
  );

  return (
    <div className="px-4 lg:px-8 pb-8 pt-6 lg:pt-8 space-y-6">
      {/* Top Header & League Type Tabs */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Trophy className="w-8 h-8 text-yellow-500" />
            {t('nav.league')}
          </h1>
        </div>

        {/* League Type Switcher */}
        <div className="flex items-center gap-3">

          <div className="bg-[#1a1a1b] p-1 rounded-lg inline-flex">
            <button
              onClick={() => {
                setActiveLeagueType('unilig');
                setSelectedSeasonId('auto');
              }}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${activeLeagueType === 'unilig'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              Ünilig
            </button>
            <button
              onClick={() => {
                setActiveLeagueType('prolig');
                setSelectedSeasonId('auto');
              }}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${activeLeagueType === 'prolig'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              Prolig
            </button>
          </div>
        </div>

        {isAdmin && (
          <Button
            onClick={() => {
              setSeasonForm(prev => ({ ...prev, league_type: activeLeagueType, division: activeDivision }));
              setShowSeasonForm(true);
            }}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" /> New Season
          </Button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-900/20 border border-red-800 rounded-lg text-red-400 flex justify-between">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Season Info / Selector */}
      <div className="text-gray-400 flex items-center gap-2">
        {displayedSeason ? (
          <>
            <Calendar className="w-4 h-4" />
            <span className="font-medium text-white">{displayedSeason.name}</span>
            <span>({displayedSeason.year})</span>
            <span className={`text-xs px-2 py-0.5 rounded ${displayedSeason.status === 'active' ? 'bg-green-900/50 text-green-400' : 'bg-gray-800'}`}>
              {displayedSeason.status}
            </span>
          </>
        ) : (
          <span>No season found for {activeLeagueType === 'unilig' ? 'Ünilig' : 'Prolig'} {activeDivision === 'div_1' ? '1. Lig' : '2. Lig'}.</span>
        )}
      </div>

      {/* Division Sub-Tabs */}
      <Tabs defaultValue="div_1" value={activeDivision} onValueChange={setActiveDivision} className="w-full">
        <TabsList className="bg-[#1a1a1b] w-full justify-start border-b border-gray-800 rounded-none p-0 h-auto">
          <TabsTrigger
            value="div_1"
            className="px-6 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-blue-500"
          >
            1. Lig
          </TabsTrigger>
          <TabsTrigger
            value="div_2"
            className="px-6 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:text-blue-500"
          >
            2. Lig
          </TabsTrigger>
          {isAdmin && (
            <div className="ml-auto flex items-center pr-4">
              {displayedSeason && (() => {
                const isPub = displayedSeason.is_published || false;
                return (
                  <Button
                    size="sm"
                    variant="ghost"
                    className={`h-7 px-2 ${isPub ? 'text-green-400' : 'text-yellow-500'}`}
                    onClick={handleTogglePublish}
                    title={isPub ? "Published (Visible to all)" : "Draft (Admin only)"}
                  >
                    {isPub ? <Eye className="w-3.5 h-3.5 mr-1" /> : <EyeOff className="w-3.5 h-3.5 mr-1" />}
                    <span className="text-[10px] uppercase font-bold">
                      {isPub ? 'Published' : 'Draft'}
                    </span>
                  </Button>
                );
              })()}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-red-500 hover:text-red-400 hover:bg-red-500/10 ml-2"
                onClick={handleDeleteSeason}
                title="Delete Season"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                <span className="text-[10px] uppercase font-bold">Delete</span>
              </Button>
            </div>
          )}
        </TabsList>

        {/* Content Area */}
        <div className="mt-6">
          {(() => {
            const isPub = displayedSeason?.is_published || false;

            if (!isAdmin && (!displayedSeason || !isPub)) {
              return (
                <div className="flex flex-col items-center justify-center py-20 bg-[#1a1a1b] border border-gray-800 rounded-xl">
                  <div className="w-16 h-16 bg-blue-600/10 rounded-full flex items-center justify-center mb-6">
                    <Calendar className="w-8 h-8 text-blue-500" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Yakında Sizlerle!</h2>
                  <p className="text-gray-400 text-center max-w-md px-6">
                    Bu lig bölümünün hazırlıkları devam ediyor. Lig sonuçları ve fikstür çok yakında burada paylaşılacaktır. Takipte kalın!
                  </p>
                </div>
              );
            }

            return (
              <>
                {/* Groups & Standings Header */}
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-white">Standings</h3>
                  {isAdmin && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => setShowGroupForm(true)} className="bg-blue-600">
                        <Plus className="w-4 h-4 mr-2" /> Add Group
                      </Button>
                    </div>
                  )}
                </div>

                {displayedSeason && displayedSeason.format !== 'single_group' && (
                  <div className="mb-8 border border-gray-800 rounded-xl bg-[#0a0a0b] p-4 overflow-hidden">
                    <div className="flex justify-center">
                      <TournamentBracket seasonId={displayedSeason.id} isAdmin={isAdmin} />
                    </div>
                  </div>
                )}

                {/* Groups & Standings Grid / Single Group Layout */}
                {displayedSeason?.format === 'single_group' ? (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-8">
                    {/* Left: Standings Table */}
                    <div className="lg:col-span-7 xl:col-span-8">
                      {filteredGroups.map(group => (
                        <Card key={group.id} className="bg-[#0a0a0b] border-gray-800 shadow-2xl">
                          <CardHeader className="py-6 border-b border-gray-800/50 flex flex-row items-center justify-between">
                            <div>
                              <CardTitle className="text-white text-2xl font-black tracking-tight">{group.name}</CardTitle>
                              <p className="text-gray-500 text-xs mt-1 uppercase tracking-widest font-bold">Current Standings</p>
                            </div>
                            {isAdmin && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeleteGroup(group.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </CardHeader>
                          <CardContent className="p-0">
                            {standings[group.id]?.standings?.length > 0 ? (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-[#111112] text-gray-400 border-b border-gray-800">
                                      <th className="py-4 pl-6 text-left w-12 text-[10px] font-black uppercase tracking-widest">#</th>
                                      <th className="py-4 text-left text-[10px] font-black uppercase tracking-widest">Team</th>
                                      <th className="py-4 text-center w-16 text-[10px] font-black uppercase tracking-widest">W</th>
                                      <th className="py-4 text-center w-16 text-[10px] font-black uppercase tracking-widest">L</th>
                                      <th className="py-4 text-center w-16 text-[10px] font-black uppercase tracking-widest">+/-</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-800/30">
                                    {standings[group.id].standings.map((team, idx) => (
                                      <tr key={team.team_id || team.team_name} className="hover:bg-blue-600/5 transition-colors group">
                                        <td className="py-4 pl-6">
                                          <span className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold ${idx === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                                            idx === 1 ? 'bg-gray-400/20 text-gray-400' :
                                              idx === 2 ? 'bg-amber-700/20 text-amber-700' : 'bg-gray-800/50 text-gray-500'
                                            }`}>
                                            {idx + 1}
                                          </span>
                                        </td>
                                        <td className="py-4 font-semibold text-white group-hover:text-blue-400 transition-colors">{team.team_name}</td>
                                        <td className="py-4 text-center font-mono text-green-400 font-bold">{team.wins}</td>
                                        <td className="py-4 text-center font-mono text-red-400 font-bold">{team.losses}</td>
                                        <td className="py-4 text-center font-mono text-gray-400">{team.point_diff}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="py-12 text-center text-gray-500 italic">No teams in standings.</div>
                            )}

                            {isAdmin && (
                              <div className="p-6 border-t border-gray-800 bg-[#111112]/50">
                                <Label className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-3 block">Manager Controls</Label>
                                <div className="flex gap-2">
                                  <Input
                                    placeholder="Enter Custom Team Name"
                                    className="bg-[#0a0a0b] border-gray-700 text-white focus:border-blue-500"
                                    value={customTeamInputs[group.id] || ''}
                                    onChange={(e) => setCustomTeamInputs({ ...customTeamInputs, [group.id]: e.target.value })}
                                  />
                                  <Button
                                    className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/10 px-6"
                                    onClick={() => {
                                      if (!customTeamInputs[group.id]) return;
                                      handleAddTeamToGroup(group.id, { team_custom_name: customTeamInputs[group.id] });
                                      setCustomTeamInputs({ ...customTeamInputs, [group.id]: '' });
                                    }}
                                  >
                                    <Plus className="w-4 h-4 mr-2" /> Add Team
                                  </Button>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-4">
                                  {group.teams.map(gt => (
                                    <div key={gt.id} className="group/tag inline-flex items-center gap-1.5 bg-gray-800/50 hover:bg-gray-800 border border-gray-700 px-2.5 py-1 rounded-full text-[11px] text-gray-300 transition-all">
                                      {gt.team_name}
                                      <button onClick={() => handleRemoveMember(group.id, gt.id)} className="text-gray-500 hover:text-red-400 transition-colors">
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Right: Fixtures / Matches */}
                    <div className="lg:col-span-5 xl:col-span-4">
                      <Card className="bg-[#0a0a0b] border-gray-800 sticky top-24 overflow-hidden">
                        <CardHeader
                          className="py-6 border-b border-gray-800/50 flex flex-row items-center justify-between cursor-pointer hover:bg-white/5 transition-colors z-10 relative bg-[#0a0a0b]"
                          onClick={() => toggleGroupExpansion('header_fixtures')}
                        >
                          <div>
                            <CardTitle className="text-white text-xl font-bold">Fixtures</CardTitle>
                            <p className="text-gray-500 text-[10px] mt-1 uppercase tracking-widest font-black">Upcoming & Recent Matches</p>
                          </div>
                          <div className="flex items-center gap-3">
                            {isAdmin && (
                              <Button
                                size="sm"
                                className="bg-blue-600/10 text-blue-400 hover:bg-blue-600 hover:text-white border border-blue-600/20 transition-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const firstGroup = filteredGroups[0];
                                  if (!firstGroup) return;
                                  setMatchForm(prev => ({ ...prev, group_id: firstGroup.id }));
                                  setShowMatchForm(true);
                                }}
                              >
                                <Plus className="w-3.5 h-3.5 mr-1" /> Match
                              </Button>
                            )}
                            {expandedGroups['header_fixtures'] ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                          </div>
                        </CardHeader>
                        {expandedGroups['header_fixtures'] !== false && (
                          <CardContent className="p-0 animate-in fade-in slide-in-from-top-4 duration-300">
                            <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
                              {filteredGroups.length > 0 ? (
                                <div className="divide-y divide-gray-800/50">
                                  {matches.filter(m => m.group_id === filteredGroups[0].id).length > 0 ? (
                                    matches.filter(m => m.group_id === filteredGroups[0].id)
                                      .sort((a, b) => new Date(b.match_date || 0) - new Date(a.match_date || 0))
                                      .map(match => (
                                        <div key={match.id} className="p-4 hover:bg-gray-800/20 transition-all">
                                          <div className="flex items-center justify-between gap-4">
                                            <div className="flex-1 text-right">
                                              <p className="text-white text-sm font-bold truncate">{match.team_a?.name || match.team_a_custom_name || 'TBD'}</p>
                                            </div>
                                            <div className="flex flex-col items-center gap-1 min-w-[80px]">
                                              {editingScore === match.id ? (
                                                <div className="flex items-center gap-2">
                                                  <Input value={scoreForm.score_a} onChange={e => setScoreForm({ ...scoreForm, score_a: parseInt(e.target.value) })} className="w-10 h-7 text-center p-1 bg-gray-900" />
                                                  <span className="text-gray-500">:</span>
                                                  <Input value={scoreForm.score_b} onChange={e => setScoreForm({ ...scoreForm, score_b: parseInt(e.target.value) })} className="w-10 h-7 text-center p-1 bg-gray-900" />
                                                </div>
                                              ) : (
                                                <div
                                                  className={`px-3 py-1 rounded bg-gray-900 border border-gray-800 text-sm font-black tabular-nums text-white ${isAdmin ? 'cursor-pointer hover:border-blue-500' : ''}`}
                                                  onClick={() => {
                                                    if (isAdmin) {
                                                      setEditingScore(match.id);
                                                      setScoreForm({ score_a: match.score_a || 0, score_b: match.score_b || 0 });
                                                    }
                                                  }}
                                                >
                                                  {match.score_a !== null ? `${match.score_a} : ${match.score_b}` : 'VS'}
                                                </div>
                                              )}
                                              <p className="text-[9px] text-gray-500 uppercase font-black">
                                                {match.match_date ? new Date(match.match_date).toLocaleDateString() : 'TBD'}
                                              </p>
                                            </div>
                                            <div className="flex-1 text-left">
                                              <p className="text-white text-sm font-bold truncate">{match.team_b?.name || match.team_b_custom_name || 'TBD'}</p>
                                            </div>
                                          </div>
                                          {isAdmin && (
                                            <div className="flex justify-center gap-3 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                              <button onClick={() => handleDeleteMatch(match.id)} className="text-gray-600 hover:text-red-500 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      ))
                                  ) : (
                                    <div className="py-12 text-center text-gray-600 flex flex-col items-center gap-2">
                                      <Calendar className="w-8 h-8 opacity-20" />
                                      <p className="text-xs">No matches scheduled.</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="p-8 text-center text-gray-500 italic">Please create a group first.</div>
                              )}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                    {filteredGroups.map(group => (
                      <Card key={group.id} className="bg-[#1a1a1b] border-gray-800 h-full overflow-hidden">
                        <CardHeader className="py-4 border-b border-gray-800">
                          <CardTitle className="text-white text-lg flex justify-between items-center">
                            <span>{group.name}</span>
                            {isAdmin && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-dashed border-gray-600 text-gray-400 hover:text-white hover:border-blue-500"
                                  onClick={() => {
                                    setMatchForm(prev => ({ ...prev, group_id: group.id, team_a_id: '', team_b_id: '', match_date: '' }));
                                    setShowMatchForm(true);
                                  }}
                                >
                                  <Calendar className="w-3 h-3 mr-1" />
                                  Fixture
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-gray-500 hover:text-red-500 hover:bg-red-500/10"
                                  onClick={() => handleDeleteGroup(group.id)}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            )}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                          {/* Standings Table */}
                          {standings[group.id]?.standings?.length > 0 ? (
                            <table className="w-full text-sm">
                              <thead className="bg-[#0a0a0b]">
                                <tr className="text-gray-400">
                                  <th className="py-2 pl-4 text-left">#</th>
                                  <th className="py-2 text-left">Team</th>
                                  <th className="py-2 text-center">W</th>
                                  <th className="py-2 text-center">L</th>
                                  <th className="py-2 text-center">+/-</th>
                                </tr>
                              </thead>
                              <tbody>
                                {standings[group.id].standings.map((team, idx) => (
                                  <tr key={team.team_id || team.team_name} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                                    <td className="py-2 pl-4 text-gray-400 font-mono">{idx + 1}</td>
                                    <td className="py-2 font-medium text-white">{team.team_name}</td>
                                    <td className="py-2 text-center text-green-400">{team.wins}</td>
                                    <td className="py-2 text-center text-red-400">{team.losses}</td>
                                    <td className="py-2 text-center text-gray-400">{team.point_diff}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="p-6 text-center text-gray-500 text-sm">No teams in this group.</p>
                          )}

                          {/* Group Matches - Collapsible */}
                          <div className="border-t border-gray-800 bg-[#0a0a0b]/30">
                            <button
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors z-10 relative bg-[#111112]"
                              onClick={() => toggleGroupExpansion(group.id)}
                            >
                              <h4 className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-2 tracking-wider">
                                <Calendar className="w-3 h-3" /> FIKSTÜR
                              </h4>
                              {expandedGroups[group.id] ? <ChevronUp className="w-3 h-3 text-gray-500" /> : <ChevronDown className="w-3 h-3 text-gray-500" />}
                            </button>

                            {expandedGroups[group.id] && (
                              <div className="p-4 pt-0 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                                {matches.filter(m => m.group_id === group.id).length > 0 ? (
                                  matches.filter(m => m.group_id === group.id).map(match => (
                                    <div key={match.id} className="flex items-center justify-between text-xs gap-2 group">
                                      <div className="flex-1 text-right truncate text-gray-300">
                                        {match.team_a?.name || match.team_a_custom_name || 'TBD'}
                                      </div>
                                      <div
                                        className={`px-2 py-0.5 rounded bg-gray-900 border border-gray-800 tabular-nums min-w-[45px] text-center text-white ${isAdmin ? 'cursor-pointer hover:border-blue-500' : ''}`}
                                        onClick={() => {
                                          if (isAdmin) {
                                            setEditingScore(match.id);
                                            setScoreForm({ score_a: match.score_a || 0, score_b: match.score_b || 0 });
                                          }
                                        }}
                                      >
                                        {match.score_a !== null ? `${match.score_a}:${match.score_b}` : 'VS'}
                                      </div>
                                      <div className="flex-1 text-left truncate text-gray-300">
                                        {match.team_b?.name || match.team_b_custom_name || 'TBD'}
                                      </div>
                                      {isAdmin && (
                                        <button onClick={() => handleDeleteMatch(match.id)} className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-500 transition-all">
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-[10px] text-gray-600 italic text-center">Henüz maç planlanmadı.</p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Admin Controls */}
                          {isAdmin && (
                            <div className="p-4 border-t border-gray-800">
                              <div className="space-y-2 mb-2">
                                <div className="flex gap-1">
                                  <Select
                                    value={selectedTeamInputs[group.id] || ''}
                                    onValueChange={(v) => setSelectedTeamInputs({ ...selectedTeamInputs, [group.id]: v })}
                                  >
                                    <SelectTrigger className="h-8 text-xs bg-[#1a1a1b] border-gray-700 flex-1 text-gray-300">
                                      <SelectValue placeholder="Select Existing Team" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                                      {teams.map(t => (
                                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <button
                                    className="h-8 w-8 rounded-md bg-blue-600 hover:bg-blue-700 flex shrink-0 items-center justify-center text-white"
                                    onClick={() => {
                                      if (!selectedTeamInputs[group.id]) return;
                                      handleAddTeamToGroup(group.id, { team_id: selectedTeamInputs[group.id] });
                                      setSelectedTeamInputs({ ...selectedTeamInputs, [group.id]: '' });
                                    }}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                                <div className="flex gap-1">
                                  <Input
                                    placeholder="Or Add Custom Team Name"
                                    className="h-8 text-xs bg-[#1a1a1b] border-gray-700 flex-1"
                                    value={customTeamInputs[group.id] || ''}
                                    onChange={(e) => setCustomTeamInputs({ ...customTeamInputs, [group.id]: e.target.value })}
                                  />
                                  <button
                                    className="h-8 w-8 rounded-md bg-blue-600 hover:bg-blue-700 flex shrink-0 items-center justify-center text-white"
                                    onClick={() => {
                                      if (!customTeamInputs[group.id]) return;
                                      handleAddTeamToGroup(group.id, { team_custom_name: customTeamInputs[group.id] });
                                      setCustomTeamInputs({ ...customTeamInputs, [group.id]: '' });
                                    }}
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-1 mt-2">
                                {group.teams.map(gt => (
                                  <span key={gt.id} className="text-[10px] bg-gray-800 text-gray-300 px-2 py-0.5 rounded flex items-center gap-1">
                                    {gt.team_name}
                                    <X className="w-2.5 h-2.5 cursor-pointer hover:text-red-400" onClick={() => handleRemoveMember(group.id, gt.id)} />
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                {filteredGroups.length === 0 && (
                  <div className="w-full py-12 text-center border border-dashed border-gray-800 rounded-lg col-span-full">
                    <Users className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-500">No {activeDivision === 'div_1' ? '1. Lig' : '2. Lig'} groups found for {displayedSeason?.name || 'this season'}.</p>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </Tabs>

      {/* Admin Forms (Overlays) */}
      {
        showSeasonForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <Card className="w-[500px] bg-[#1a1a1b] border-gray-800">
              <CardHeader><CardTitle className="text-white">New Season</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleCreateSeason} className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Name</Label>
                    <Input value={seasonForm.name} onChange={e => setSeasonForm({ ...seasonForm, name: e.target.value })} className="bg-[#0a0a0b] text-white border-gray-700" placeholder="e.g. 2025 Fall" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">League Type</Label>
                      <Select value={seasonForm.league_type} onValueChange={v => setSeasonForm({ ...seasonForm, league_type: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                          <SelectItem value="unilig">Ünilig</SelectItem>
                          <SelectItem value="prolig">Prolig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-300">Division</Label>
                      <Select value={seasonForm.division} onValueChange={v => setSeasonForm({ ...seasonForm, division: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                          <SelectItem value="div_1">1. Lig</SelectItem>
                          <SelectItem value="div_2">2. Lig</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Format</Label>
                      <Select value={seasonForm.format} onValueChange={v => setSeasonForm({ ...seasonForm, format: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                          <SelectItem value="single_group">Single Group</SelectItem>
                          <SelectItem value="groups_son16">Groups + Son 16</SelectItem>
                          <SelectItem value="groups_son8">Groups + Son 8</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {seasonForm.format !== 'single_group' && (
                      <div>
                        <Label className="text-gray-300">Group Count</Label>
                        <Input type="number" value={seasonForm.group_count} onChange={e => setSeasonForm({ ...seasonForm, group_count: parseInt(e.target.value) })} className="bg-[#0a0a0b] text-white border-gray-700" min="1" max="16" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-gray-300">Year</Label>
                    <Input type="number" value={seasonForm.year} onChange={e => setSeasonForm({ ...seasonForm, year: parseInt(e.target.value) })} className="bg-[#0a0a0b] text-white border-gray-700" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => setShowSeasonForm(false)} className="text-gray-400 hover:text-white hover:bg-white/10">Cancel</Button>
                    <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">Create</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )
      }

      {
        showGroupForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <Card className="w-[500px] bg-[#1a1a1b] border-gray-800">
              <CardHeader><CardTitle className="text-white">New Group</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleCreateGroup} className="space-y-4">
                  <div>
                    <Label className="text-gray-300">Division</Label>
                    <Select value={groupForm.division} onValueChange={v => setGroupForm({ ...groupForm, division: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                        <SelectItem value="div_1">1. Lig</SelectItem>
                        <SelectItem value="div_2">2. Lig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-300">Group Name</Label>
                    <Input value={groupForm.name} onChange={e => setGroupForm({ ...groupForm, name: e.target.value })} className="bg-[#0a0a0b] text-white border-gray-700" placeholder="Group A" />
                  </div>
                  <div>
                    <Label className="text-gray-300">Stage</Label>
                    <Select value={groupForm.stage} onValueChange={v => setGroupForm({ ...groupForm, stage: v })}>
                      <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                        <SelectItem value="group">Group Stage</SelectItem>
                        <SelectItem value="playoff">Playoffs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => setShowGroupForm(false)} className="text-gray-400 hover:text-white hover:bg-white/10">Cancel</Button>
                    <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">Create</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )
      }

      {
        showMatchForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <Card className="w-[600px] bg-[#1a1a1b] border-gray-800">
              <CardHeader><CardTitle className="text-white">Create Match</CardTitle></CardHeader>
              <CardContent>
                <form onSubmit={handleCreateMatch} className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-gray-300">Group (Optional)</Label>
                    <Select value={matchForm.group_id} onValueChange={v => setMatchForm({ ...matchForm, group_id: v, team_a_id: '', team_b_id: '' })}>
                      <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700"><SelectValue placeholder="Select Group" /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                        {groups.filter(g => g.division === activeDivision).map(g => (
                          <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Team A Selection */}
                  <div>
                    <Label className="text-gray-300 flex justify-between">
                      Team A
                      <span className="text-xs text-blue-400 cursor-pointer hover:underline" onClick={() => setMatchForm(prev => ({ ...prev, isCustomA: !prev.isCustomA, team_a_id: '', team_a_custom_name: '' }))}>
                        {matchForm.isCustomA ? 'Select Team' : 'Custom Name'}
                      </span>
                    </Label>
                    {matchForm.isCustomA ? (
                      <Input
                        value={matchForm.team_a_custom_name || ''}
                        onChange={e => setMatchForm({ ...matchForm, team_a_custom_name: e.target.value })}
                        className="bg-[#0a0a0b] text-white border-gray-700"
                        placeholder="Enter Team Name"
                      />
                    ) : (
                      <Select value={matchForm.team_a_id} onValueChange={v => setMatchForm({ ...matchForm, team_a_id: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700"><SelectValue placeholder="Select Team" /></SelectTrigger>
                        <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                          {availableMatchTeams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Team B Selection */}
                  <div>
                    <Label className="text-gray-300 flex justify-between">
                      Team B
                      <span className="text-xs text-blue-400 cursor-pointer hover:underline" onClick={() => setMatchForm(prev => ({ ...prev, isCustomB: !prev.isCustomB, team_b_id: '', team_b_custom_name: '' }))}>
                        {matchForm.isCustomB ? 'Select Team' : 'Custom Name'}
                      </span>
                    </Label>
                    {matchForm.isCustomB ? (
                      <Input
                        value={matchForm.team_b_custom_name || ''}
                        onChange={e => setMatchForm({ ...matchForm, team_b_custom_name: e.target.value })}
                        className="bg-[#0a0a0b] text-white border-gray-700"
                        placeholder="Enter Team Name"
                      />
                    ) : (
                      <Select value={matchForm.team_b_id} onValueChange={v => setMatchForm({ ...matchForm, team_b_id: v })}>
                        <SelectTrigger className="bg-[#0a0a0b] text-white border-gray-700"><SelectValue placeholder="Select Team" /></SelectTrigger>
                        <SelectContent className="bg-[#1a1a1b] border-gray-700 text-white">
                          {availableMatchTeams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="col-span-2">
                    <Label className="text-gray-300">Date</Label>
                    <Input type="datetime-local" value={matchForm.match_date} onChange={e => setMatchForm({ ...matchForm, match_date: e.target.value })} className="bg-[#0a0a0b] text-white border-gray-700" />
                  </div>
                  <div className="col-span-2 flex justify-end gap-2">
                    <Button type="button" variant="ghost" onClick={() => setShowMatchForm(false)} className="text-gray-400 hover:text-white hover:bg-white/10">Cancel</Button>
                    <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white">Create</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )
      }

      {
        editingScore && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <Card className="w-[300px] bg-[#1a1a1b] border-gray-800">
              <CardHeader><CardTitle className="text-white text-center">Update Score</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Input type="number" value={scoreForm.score_a} onChange={e => setScoreForm({ ...scoreForm, score_a: parseInt(e.target.value) })} className="w-16 text-center bg-[#0a0a0b]" />
                  <span>:</span>
                  <Input type="number" value={scoreForm.score_b} onChange={e => setScoreForm({ ...scoreForm, score_b: parseInt(e.target.value) })} className="w-16 text-center bg-[#0a0a0b]" />
                </div>
                <div className="flex justify-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditingScore(null)}>Cancel</Button>
                  <Button size="sm" className="bg-green-600" onClick={() => handleUpdateScore(editingScore)}>Save</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )
      }
    </div >
  );
};

export default League;
